import {
  ApplicationConfig,
  LOCALE_ID,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideExperimentalZonelessChangeDetection
} from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import {
  provideHttpClient,
  withFetch,
  withInterceptors
} from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { GlobalFacade } from '~backoffice/app/~store/facades/global.facade';
import { AuthFacade } from '~backoffice/app/~store/facades/auth.facade';
import { provideHotToastConfig } from '@ngneat/hot-toast';
import {
  popperVariation,
  provideTippyConfig,
  tooltipVariation
} from '@ngneat/helipopper';
import { NgxsModule } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { GlobalState } from '~backoffice/app/~store/states/global.state';
import { NgxsActionsExecutingModule } from '@ngxs-labs/actions-executing';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import {
  AuthState,
  DEFAULT_AUTH_STATE
} from '~backoffice/app/~store/states/auth.state';
import { NgxsStoragePluginModule, STORAGE_ENGINE } from '@ngxs/storage-plugin';
import { LayoutModule } from '@angular/cdk/layout';
import { environment } from '~environments/environment';
import { CustomNgxsStorageEngine } from './~shared/utils/storage';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { provideClientHydration } from '@angular/platform-browser';
import { httpErrorsInterceptor } from './~shared/interceptors/http-errors.interceptors';
import {
  AUTH_STATE_TOKEN,
  GLOBAL_STATE_TOKEN
} from './~shared/constants/global.constants';
import { SsrRequestService } from './~shared/services/ssr-request.service';
import { StorageService } from './~shared/services/storage.service';
import { CoreModule } from '~core/modules/core.module';
import { StorageModule } from '~core/modules/storage.module';
import { NgxsSelectSnapshotModule } from '@ngxs-labs/select-snapshot';
import { BreakpointsService } from './~shared/services/breakpoints.service';
import { Observable } from 'rxjs';
import { ConstantsService } from '~core/services/constants.service';
import { isEmpty } from 'lodash-es';
import { SnackbarService } from './~shared/services/snackbar.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { withInterceptorsFromDi } from '@angular/common/http';
import { provideLoadingBarInterceptor } from '@ngx-loading-bar/http-client';
import { provideLoadingBarRouter } from '@ngx-loading-bar/router';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { ResourceFacade } from './~store/facades/resource.facade';
import { ResourceState } from './~store/states/resource.state';
import { ThematicService } from '~core/services/thematic.service';

import { DialogService } from '~core/services/dialog.service';
import { TagService } from '~core/services/tag.service';
import { CategoryService } from '~core/services/category.service';
import { applicationIdInterceptor } from './~shared/interceptors/application-id.interceptor';
import { jwtInterceptor } from './~shared/interceptors/jwt.interceptor';
import { BadgeService } from '~core/services/badge.service';
import { AuthenticationService } from './~shared/services/authentication.service';
import { UserManagementService } from './~shared/services/user-management.service';
import { TagManagementService } from './~shared/services/tag-management.service';
import { GeoblockingService } from './~shared/services/geoblocking.service';
import { GeoblockingManagementService } from './~shared/services/geoblocking-management.service';
import { QuillModule } from 'ngx-quill';
import { ChannelService } from '~core/services/channel.service';
import { ThematicManagementService } from './~shared/services/thematic-management.service';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { ChannelManagementService } from './~shared/services/channel-management.service';
import { channelIdInterceptor } from './~shared/interceptors/channel-id.interceptor';
import { provideNativeDateAdapter } from '@angular/material/core';
import { VideoManagementService } from './~shared/services/video-management.service';
import { ContentService } from '~core/services/content.service';
import { KalturaService } from './~shared/services/kaltura.service';
import { GroupService } from '~core/services/group.service';
import { ThemingService } from './~shared/services/theming.service';
import { ProgramManagementService } from './~shared/services/program-management.service';
import { GenreService } from '~core/services/genre.service';
import { PersonService } from '~core/services/person.service';
import { UserService } from '~backoffice/app/~shared/services/user.service';
import { LiveStreamService } from '~core/services/live-stream.service';
import { LiveStreamManagementService } from './~shared/services/live-stream-management.service';

registerLocaleData(localeFr, 'fr');

export const appConfig: ApplicationConfig = {
  providers: [
    provideExperimentalZonelessChangeDetection(),
    provideClientHydration(),
    provideNativeDateAdapter(),
    provideRouter(routes),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        applicationIdInterceptor,
        channelIdInterceptor,
        jwtInterceptor,
        httpErrorsInterceptor
      ]),
      withInterceptorsFromDi()
    ),
    provideLoadingBarInterceptor(),
    provideLoadingBarRouter(),
    provideAnimations(),
    provideAngularSvgIcon(),
    { provide: LOCALE_ID, useValue: 'fr' },
    provideHotToastConfig(),
    provideTippyConfig({
      defaultVariation: 'tooltip',
      variations: {
        tooltip: tooltipVariation,
        popper: popperVariation
      }
    }),
    importProvidersFrom([
      NgxsModule.forRoot([GlobalState, AuthState, ResourceState], {
        developmentMode: !environment.production
      }),
      NgxsActionsExecutingModule.forRoot(),
      NgxsSelectSnapshotModule.forRoot(),
      NgxsFormPluginModule.forRoot(),
      NgxsStoragePluginModule.forRoot({
        keys: [GlobalState, AuthState],
        beforeSerialize: (obj, key) => {
          if (key === GLOBAL_STATE_TOKEN.getName()) {
            // Save only provided properties on storage
            return {
              routeData: { currentUrl: obj?.routeData?.currentUrl },
              currentChannel: obj?.currentChannel
            };
          }
          if (key === AUTH_STATE_TOKEN.getName()) {
            return isEmpty(obj)
              ? DEFAULT_AUTH_STATE
              : {
                  currentUser: obj?.currentUser,
                  tokens: obj?.tokens,
                  login: DEFAULT_AUTH_STATE.login,
                  registration: DEFAULT_AUTH_STATE.registration,
                  reset: DEFAULT_AUTH_STATE.reset
                };
          }
          return obj;
        }
      }),
      NgxsReduxDevtoolsPluginModule.forRoot(),
      LayoutModule,
      CoreModule,
      StorageModule,
      QuillModule,
      FontAwesomeModule,
      LazyLoadImageModule
    ]),
    provideAppInitializer((): Observable<unknown> | void => {
      const constantsService = inject(ConstantsService);

      if (!environment.local) {
        return constantsService.getRemoteEnvironmentVariables();
      }
    }),
    {
      provide: STORAGE_ENGINE,
      useClass: CustomNgxsStorageEngine
    },
    BreakpointsService,
    StorageService,
    SsrRequestService,
    GlobalFacade,
    AuthFacade,
    ResourceFacade,
    SnackbarService,
    DialogService,
    ThematicService,
    AuthenticationService,
    CategoryService,
    TagService,
    BadgeService,
    ChannelService,
    UserService,
    GeoblockingService,
    GeoblockingManagementService,
    UserManagementService,
    TagManagementService,
    AuthenticationService,
    ThematicManagementService,
    ChannelManagementService,
    VideoManagementService,
    ContentService,
    KalturaService,
    GroupService,
    ThemingService,
    ProgramManagementService,
    GenreService,
    PersonService,
    LiveStreamService,
    LiveStreamManagementService
  ]
};
